import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import useTracking from "@repo/shopify/hooks/useTracking";
import React, { useCallback, useEffect, useRef } from "react";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { NOTIFICATIONS } from "@const/notifications";

import useCustomerQuery from "@hooks/useCustomerQuery";
import useLanguage from "@hooks/useLanguage";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

const ACCESS_TOKEN: Record<string, string> = {
  ko: import.meta.env.VITE_KR_STOREFRONT_ACCESS_TOKEN,
  jp: import.meta.env.VITE_JP_STOREFRONT_ACCESS_TOKEN,
  en: import.meta.env.VITE_INTL_STOREFRONT_ACCESS_TOKEN,
};

const STORE_URL = {
  ko: "thisisneverthatstore.myshopify.com",
  jp: "thisisneverthat-jp.myshopify.com",
  en: "thisisneverthat-intl.myshopify.com",
};

interface ICustomer {
  id: string;
  email: string;
  accessToken: string;
}

const DEFAULT_PATH = "/js/hiko-auth-headless.js";
const BASE_URL = "https://apps.hiko.link";

export function SocialLogin() {
  const navigate = useNavigate();

  const { lang } = useLanguage();

  const { isPC } = useCheckDevice();

  const { openToast, openModal, closeAll } = useLayoutStore();
  const { login, logout } = useCustomerStore();

  const { fetchCustomer, deleteAddress } = useCustomerQuery();
  const { gtmLogin } = useTracking();

  const logoutCallbackRef = useRef<(() => void) | null>(null);
  const refreshCallbackRef = useRef<(() => void) | null>(null);

  const container = useRef<HTMLDivElement>(null);

  const shop = STORE_URL[lang];
  const publicAccessToken = ACCESS_TOKEN[lang];

  useEffect(() => {
    document.addEventListener("hiko", handleCustomEvents);
    return () => {
      document.removeEventListener("hiko", handleCustomEvents);
    };
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = `${BASE_URL}${DEFAULT_PATH}`;
    script.async = true;

    // 1. MutationObserver 생성: container 내부에 변화가 생길 때마다 실행
    const observer = new MutationObserver((mutations, obs) => {
      // container 하위에서 요소 찾기 (document 전체 검색보다 빠르고 안전함)
      const kakaoText = container.current?.querySelector(".hkakao_text");
      const googleText = container.current?.querySelector(".hgoogle_text");
      const naverText = container.current?.querySelector(".hnaver_text");

      if (isPC) return;

      if (kakaoText) kakaoText.textContent = "Kakao";
      if (googleText) googleText.textContent = "Google";
      if (naverText) naverText.textContent = "Naver";

      obs.disconnect(); // 텍스트를 변경했으면 더 이상 감시할 필요가 없으므로 옵저버 해제
    });

    // 2. container에 옵저버 연결 (자식 요소와 하위 트리 변화 감시)
    if (container.current) {
      observer.observe(container.current, { childList: true, subtree: true });
    }

    script.onload = () => {
      window.HIKO.render(container.current, shop, publicAccessToken);
    };

    document.head.appendChild(script);

    logoutCallbackRef.current = () => {
      window.HIKO.logout();
      window.HIKO.render(container.current, shop, publicAccessToken);
    };

    refreshCallbackRef.current = () => {
      const found = document.querySelector(`script[src*="${DEFAULT_PATH}"]`);
      if (found) {
        found.remove();
        window.HIKO.release();
      }
    };

    // 컴포넌트 언마운트 시 클린업
    return () => {
      observer.disconnect();
    };
  }, []); // isPC가 실시간 리사이징으로 변할 수 있다면 의존성 배열(deps)이나 별도의 useEffect를 고려해야 할 수 있습니다.

  const handleCustomEvents = useCallback(async (event: any) => {
    sessionStorage.removeItem("ENP_CUSTOM.B.jknd_tnt");
    sessionStorage.removeItem("check_uni");

    const { action } = event.detail;

    if (["login", "activate", "multipass"].includes(event.detail.action)) {
      try {
        // customer가 Promise인 경우 resolve
        const customer = (await Promise.resolve(event.detail.customer)) as ICustomer;
        login({ expiresAt: "", accessToken: customer.accessToken });

        // customer 정보 조회 후 회원정보 저장
        const res = await fetchCustomer({ variables: { customerAccessToken: customer.accessToken } });

        if (!res?.data?.customer) {
          openToast(NOTIFICATIONS.ERROR_SERVER);
          logout();
          logoutCallbackRef.current?.();
          return;
        }

        if (res?.data?.customer?.tags.includes("탈퇴요청")) {
          openModal({
            title: "Account Access Restricted",
            content: (
              <div>
                <Trans i18nKey="login.account_disable" />
              </div>
            ),
            confirm: {
              label: "Confirm",
              onClick: () => {
                openModal(null);
                closeAll();
              },
            },
          });
          logout();
          logoutCallbackRef.current?.();
          return;
        }

        const TOSS_EMAILS = ["toss.im", "tosspayments.com", "tossinsight.im"];
        if (TOSS_EMAILS.includes((res.data.customer?.email || "").split("@")[1])) {
          if (!res.data.customer.tags.includes("PARTNERSHIP_TOSS")) {
            openModal({
              title: "Verify Your Account",
              content:
                "토스 임직원 계정은 이메일 인증을 완료해야 할인 혜택이 적용됩니다. 이메일 수신함을 확인해 주세요.",
              isHideCancelButton: true,
              confirm: {
                label: "Okay",
                onClick: () => {
                  openModal(null);
                },
              },
            });
          }
        }

        const loginAfterUrl = sessionStorage.getItem("loginAfterUrl");
        if (loginAfterUrl) {
          navigate(loginAfterUrl);
        }

        sessionStorage.removeItem("loginAfterUrl");

        closeAll();
        openToast(NOTIFICATIONS.SUCCESS_LOGIN);

        gtmLogin(
          "social",
          res.data.customer.id.replace("gid://shopify/Customer/", ""),
          res.data.customer.email ?? undefined,
        );

        // action이 activate인 경우(=register) null address 삭제
        if (event.detail.action === "activate" && res.data.customer.addresses?.edges) {
          res?.data?.customer?.addresses?.edges
            ?.filter(({ node }) => !node.address1)
            .forEach(({ node }) => void deleteAddress({ variables: { id: node.id } }));
        }

        logoutCallbackRef.current?.();
      } catch (error) {
        openToast(NOTIFICATIONS.ERROR_SERVER);
        logout();
        logoutCallbackRef.current?.();
        const message = error instanceof Error ? error.message : String(error);
        throw new Error(message);
      }
    } else if (action !== "click") {
      console.log("[SocialLogin] Unhandled action", { action });
    }
  }, []);

  return <div ref={container} />;
}
